import type {
  NotificationItemProps,
  NotificationType,
  NotificationWithUser,
} from '@/types/notification'
import {
  Bell,
  Calendar,
  Check,
  CheckCircle,
  Eye,
  Megaphone,
  PlayCircle,
  Trophy,
  X,
} from 'lucide-react'
import * as React from 'react'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { cn } from '@/lib/utils'

const iconMap: Record<NotificationType, React.ComponentType<{ className?: string }>> = {
  candidate_added_referees: Bell,
  referee_viewed_reference: Eye,
  referee_started_reference: PlayCircle,
  referee_completed_reference: CheckCircle,
  reference_process_completed: Trophy,
  system_announcement: Megaphone,
  weekly_summary: Calendar,
  reference_request_ready_to_start: CheckCircle,
}

const iconColorMap: Record<NotificationType, string> = {
  candidate_added_referees: 'text-blue-500',
  referee_viewed_reference: 'text-purple-500',
  referee_started_reference: 'text-yellow-500',
  referee_completed_reference: 'text-green-500',
  reference_process_completed: 'text-emerald-500',
  system_announcement: 'text-red-500',
  weekly_summary: 'text-indigo-500',
  reference_request_ready_to_start: 'text-green-500',
}

function getRelativeTime(date: Date | string): string {
  const now = new Date()
  const then = new Date(date)
  const seconds = Math.floor((now.getTime() - then.getTime()) / 1000)

  if (seconds < 60)
    return 'just now'
  if (seconds < 3600)
    return `${Math.floor(seconds / 60)} minute${Math.floor(seconds / 60) === 1 ? '' : 's'} ago`
  if (seconds < 86400)
    return `${Math.floor(seconds / 3600)} hour${Math.floor(seconds / 3600) === 1 ? '' : 's'} ago`
  if (seconds < 604800)
    return `${Math.floor(seconds / 86400)} day${Math.floor(seconds / 86400) === 1 ? '' : 's'} ago`
  if (seconds < 2592000)
    return `${Math.floor(seconds / 604800)} week${Math.floor(seconds / 604800) === 1 ? '' : 's'} ago`
  return `${Math.floor(seconds / 2592000)} month${Math.floor(seconds / 2592000) === 1 ? '' : 's'} ago`
}

function getUserInitials(name?: string): string {
  if (!name)
    return '?'
  const parts = name.split(' ')
  if (parts.length === 1)
    return parts[0].charAt(0).toUpperCase()
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase()
}

export const NotificationItem = React.forwardRef<HTMLDivElement, NotificationItemProps>(
  (
    {
      notification,
      onMarkAsRead,
      onMarkAsUnread,
      onClick,
      className,
      selected,
      onSelect,
      showUnreadAction = true,
    },
    ref,
  ) => {
    const Icon = iconMap[notification.type] || Bell
    const iconColor = iconColorMap[notification.type] || 'text-gray-500'
    const isUnread = !notification.readAt
    const hasUser = 'user' in notification && (notification as NotificationWithUser).user

    const handleCheckboxChange = (checked: boolean | 'indeterminate') => {
      if (onSelect) {
        onSelect(notification.id, checked === true)
      }
    }

    const handleMarkAsRead = (e: React.MouseEvent) => {
      e.stopPropagation()
      if (onMarkAsRead) {
        onMarkAsRead(notification.id)
      }
    }

    const handleMarkAsUnread = (e: React.MouseEvent) => {
      e.stopPropagation()
      if (onMarkAsUnread) {
        onMarkAsUnread(notification.id)
      }
    }

    return (
      <div
        ref={ref}
        className={cn(
          'relative flex gap-3 p-4 transition-colors hover:bg-muted/50',
          isUnread && 'bg-muted/30',
          onClick && 'cursor-pointer',
          className,
        )}
        onClick={() => onClick?.(notification)}
        role="listitem"
        aria-label={`${isUnread ? 'Unread' : 'Read'} notification: ${notification.title}`}
      >
        {onSelect && (
          <div className="flex items-center">
            <Checkbox
              checked={selected}
              onCheckedChange={handleCheckboxChange}
              aria-label="Select notification"
              onClick={e => e.stopPropagation()}
            />
          </div>
        )}

        <div className="flex-shrink-0">
          {hasUser
            ? (
                <Avatar className="h-10 w-10">
                  <AvatarImage src={(notification as NotificationWithUser).user?.avatar} />
                  <AvatarFallback>
                    {getUserInitials((notification as NotificationWithUser).user?.name)}
                  </AvatarFallback>
                </Avatar>
              )
            : (
                <div
                  className={cn(
                    'flex h-10 w-10 items-center justify-center rounded-full bg-muted',
                    iconColor,
                  )}
                >
                  <Icon className="h-5 w-5" />
                </div>
              )}
        </div>

        <div className="flex-1 min-w-0 space-y-1">
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1 min-w-0">
              <p className={cn('text-sm break-words', isUnread && 'font-semibold')}>
                {notification.title}
              </p>
              <p className="mt-1 text-sm text-muted-foreground break-words">
                {notification.message}
              </p>
            </div>

            {isUnread && (
              <div
                className="h-2 w-2 flex-shrink-0 rounded-full bg-blue-500"
                aria-label="Unread indicator"
              />
            )}
          </div>

          <div className="flex items-center justify-between">
            <span className="text-xs text-muted-foreground">
              {getRelativeTime(notification.createdAt)}
            </span>

            <div className="flex items-center gap-1">
              {isUnread && onMarkAsRead && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 px-2 text-xs"
                  onClick={handleMarkAsRead}
                  aria-label="Mark as read"
                >
                  <Check className="mr-1 h-3 w-3" />
                  Mark as read
                </Button>
              )}

              {!isUnread && onMarkAsUnread && showUnreadAction && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 px-2 text-xs"
                  onClick={handleMarkAsUnread}
                  aria-label="Mark as unread"
                >
                  <X className="mr-1 h-3 w-3" />
                  Mark as unread
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    )
  },
)

NotificationItem.displayName = 'NotificationItem'
